import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { deleteItem, updateItem } from '../../services/itemsService';
import { addShare, removeShare, setThingGroups } from '../../services/thingsToGroupsService';
import Owner from '../user/Owner';
import { useOwnerGroups } from '../../hooks/useOwnerGroups';
import { useThingGroups } from '../../hooks/useThingGroups';
import { useUserProfile } from '../../hooks/useUserProfile';
import './ThingDetailPage.css';

function ThingDetailPage({ thing, user, onBack, onThingUpdated, onThingDeleted }) {
  const [editingThing, setEditingThing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAdditionalNotes, setEditAdditionalNotes] = useState('');
  const [editError, setEditError] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const isOwner = thing.user_id === user?.id;
  const { fullName: ownerName } = useUserProfile(thing.user_id);
  const { groups: ownerGroups, loading: ownerGroupsLoading } = useOwnerGroups(isOwner ? thing.user_id : null);
  const { groupIds: sharedGroupIds, refetch: refetchThingGroups } = useThingGroups(thing.id);
  const [sharingGroupIds, setSharingGroupIds] = useState([]);
  const [sharingSaving, setSharingSaving] = useState(false);
  const [sharingError, setSharingError] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestSubject, setRequestSubject] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [requestError, setRequestError] = useState(null);

  useEffect(() => {
    setSharingGroupIds(sharedGroupIds);
  }, [sharedGroupIds]);

  async function handleGroupToggle(groupId) {
    const currentlyShared = sharingGroupIds.includes(groupId);
    const previousIds = sharingGroupIds;
    const nextIds = currentlyShared
      ? previousIds.filter((id) => id !== groupId)
      : [...previousIds, groupId];
    setSharingError(null);
    setSharingGroupIds(nextIds);
    setSharingSaving(true);
    try {
      if (currentlyShared) {
        await removeShare(thing.id, groupId);
      } else {
        await addShare(thing.id, groupId);
      }
      refetchThingGroups(true);
    } catch (err) {
      setSharingGroupIds(previousIds);
      setSharingError(err.message || 'Failed to save.');
    } finally {
      setSharingSaving(false);
    }
  }

  async function handleShareWithAllGroups() {
    if (!ownerGroups?.length) return;
    const toAdd = ownerGroups.filter((g) => !sharingGroupIds.includes(g.id));
    if (toAdd.length === 0) return;
    const previousIds = sharingGroupIds;
    setSharingError(null);
    setSharingGroupIds((prev) => [...prev, ...toAdd.map((g) => g.id)]);
    setSharingSaving(true);
    try {
      await setThingGroups(thing.id, [...new Set([...sharingGroupIds, ...toAdd.map((g) => g.id)])]);
      refetchThingGroups(true);
    } catch (err) {
      setSharingGroupIds(previousIds);
      setSharingError(err.message || 'Failed to share with all groups.');
    } finally {
      setSharingSaving(false);
    }
  }

  function startEditing() {
    setEditName(thing.name ?? '');
    setEditDescription(thing.description ?? '');
    setEditAdditionalNotes(thing.additional_notes ?? '');
    setEditError(null);
    setEditingThing(true);
  }

  function cancelEditing() {
    setEditingThing(false);
    setEditError(null);
  }

  async function handleEditSave(e) {
    e.preventDefault();
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setEditError('Name is required.');
      return;
    }
    setEditError(null);
    setEditSubmitting(true);
    try {
      const data = await updateItem(thing.id, {
        name: trimmedName,
        description: editDescription.trim() || '',
        additionalNotes: editAdditionalNotes.trim() || '',
      });
      setEditingThing(false);
      onThingUpdated(data);
    } catch (err) {
      setEditError(err.message || 'Failed to update thing.');
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleDeleteConfirm() {
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      await deleteItem(thing.id);
      setDeleteConfirmOpen(false);
      onThingDeleted();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete thing.');
    } finally {
      setDeleteSubmitting(false);
    }
  }

  function openRequestModal() {
    const defaultSubject =
      thing.type === 'request'
        ? `Re: your request "${thing.name || 'request'}"`
        : `Request to borrow: ${thing.name || 'thing'}`;
    const defaultMessage =
      thing.type === 'request'
        ? 'Hi, I can help with your request.'
        : `Hi, I'd like to borrow your ${thing.name || 'item'}.`;
    setRequestSubject(defaultSubject);
    setRequestMessage(defaultMessage);
    setRequestError(null);
    setRequestModalOpen(true);
  }

  function closeRequestModal() {
    setRequestModalOpen(false);
    setRequestError(null);
  }

  async function handleSendRequest(e) {
    e.preventDefault();
    const subject = requestSubject.trim();
    const message = requestMessage.trim();
    if (!subject || !message) {
      setRequestError('Subject and message are required.');
      return;
    }
    setRequestError(null);
    setRequestSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-request-email', {
        body: { thing_id: thing.id, subject, message },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      closeRequestModal();
    } catch (err) {
      setRequestError(err.message || 'Failed to send message.');
    } finally {
      setRequestSending(false);
    }
  }

  return (
    <div className="thing-detail-page">
      <div className="thing-detail-actions">
        <button
          type="button"
          className="back-link"
          onClick={() => {
            setEditingThing(false);
            setDeleteConfirmOpen(false);
            setDeleteError(null);
            closeRequestModal();
            onBack();
          }}
        >
          ← Back
        </button>
        {thing.user_id === user?.id && !editingThing && (
          <div className="thing-detail-buttons">
            <button
              type="button"
              className="header-button"
              onClick={startEditing}
            >
              Edit
            </button>
            <button
              type="button"
              className="header-button delete-button"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      <h3 className="map-section-title">{thing.type === 'request' ? 'Request' : 'Thing'}</h3>
      <article className="thing-detail" aria-label={`${thing.type === 'request' ? 'Request' : 'Thing'}: ${thing.name}`}>
        {editingThing ? (
          <form onSubmit={handleEditSave} className="thing-detail-edit-form">
            {editError && (
              <p className="form-error" role="alert">
                {editError}
              </p>
            )}
            <label className="form-label" htmlFor="edit-thing-name">
              Name
            </label>
            <input
              id="edit-thing-name"
              type="text"
              className="form-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={thing.type === 'request' ? 'Request name' : 'Thing name'}
              required
              disabled={editSubmitting}
              autoComplete="off"
            />
            <label className="form-label" htmlFor="edit-thing-description">
              Description (optional)
            </label>
            <textarea
              id="edit-thing-description"
              className="form-input form-textarea"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description"
              rows={3}
              disabled={editSubmitting}
            />
            <label className="form-label" htmlFor="edit-thing-additional-notes">
              Additional notes (optional)
            </label>
            <textarea
              id="edit-thing-additional-notes"
              className="form-input form-textarea"
              value={editAdditionalNotes}
              onChange={(e) => setEditAdditionalNotes(e.target.value)}
              placeholder="Stipulations, quirks, or other notes"
              rows={2}
              disabled={editSubmitting}
            />
            <div className="thing-detail-edit-buttons">
              <button
                type="submit"
                className="submit-button"
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="header-button"
                onClick={cancelEditing}
                disabled={editSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <h2 className="thing-detail-name">{thing.name}</h2>
            <div className="thing-detail-description-row">
              <span className="thing-detail-prefix">Description</span>
              <span className="thing-detail-description-value">
                {thing.description || '—'}
              </span>
            </div>
            <div className="thing-detail-description-row">
              <span className="thing-detail-prefix">Additional notes</span>
              <span className="thing-detail-description-value">
                {thing.additional_notes || '—'}
              </span>
            </div>
          </>
        )}
      </article>
      {isOwner && !editingThing && (
        <section className="thing-detail-sharing" aria-label="Sharing">
          <h3 className="map-section-title">Sharing</h3>
          <div className="thing-sharing-form">
            {sharingError && <p className="form-error" role="alert">{sharingError}</p>}
            {!ownerGroupsLoading && ownerGroups.length > 0 && (
              <>
                <div className="thing-sharing-groups-header">
                  <p className="thing-sharing-groups-label">Shared with groups:</p>
                  <button
                    type="button"
                    className="header-button thing-share-all-groups-btn"
                    onClick={handleShareWithAllGroups}
                    disabled={
                      sharingSaving ||
                      ownerGroups.every((g) => sharingGroupIds.includes(g.id))
                    }
                  >
                    Share with all groups
                  </button>
                </div>
                {ownerGroups.map((g) => (
                  <div key={g.id} className="thing-sharing-checkbox-row">
                    <input
                      id={`share-group-${g.id}`}
                      type="checkbox"
                      checked={sharingGroupIds.includes(g.id)}
                      onChange={() => handleGroupToggle(g.id)}
                      disabled={sharingSaving}
                    />
                    <label className="form-label" htmlFor={`share-group-${g.id}`}>{g.name}</label>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      )}
      {!isOwner && user?.id && !editingThing && (
        <div className="thing-detail-request-contact">
          <button
            type="button"
            className="header-button thing-detail-request-btn"
            onClick={openRequestModal}
          >
            {thing.type === 'request'
              ? `Contact ${ownerName || 'owner'}`
              : 'Request thing'}
          </button>
        </div>
      )}
      <Owner userId={thing.user_id} />
      {deleteConfirmOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
        >
          <div className="modal-card">
            <h3 id="delete-modal-title" className="modal-title">
              Delete this {thing.type === 'request' ? 'request' : 'thing'}?
            </h3>
            <p className="modal-text">
              This cannot be undone.
            </p>
            {deleteError && (
              <p className="form-error modal-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="header-button"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDeleteError(null);
                }}
                disabled={deleteSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-delete-button"
                onClick={handleDeleteConfirm}
                disabled={deleteSubmitting}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {requestModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="request-modal-title"
        >
          <div className="modal-card thing-detail-request-modal">
            <h3 id="request-modal-title" className="modal-title">
              {thing.type === 'request' ? 'Contact owner' : 'Request thing'}
            </h3>
            <form onSubmit={handleSendRequest}>
              {requestError && (
                <p className="form-error modal-error" role="alert">
                  {requestError}
                </p>
              )}
              <label className="form-label" htmlFor="request-subject">
                Subject
              </label>
              <input
                id="request-subject"
                type="text"
                className="form-input"
                value={requestSubject}
                onChange={(e) => setRequestSubject(e.target.value)}
                placeholder="Subject"
                disabled={requestSending}
                autoComplete="off"
              />
              <label className="form-label" htmlFor="request-message">
                Message
              </label>
              <textarea
                id="request-message"
                className="form-input form-textarea"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Message"
                rows={4}
                disabled={requestSending}
              />
              <div className="modal-actions thing-detail-request-actions">
                <button
                  type="button"
                  className="header-button"
                  onClick={closeRequestModal}
                  disabled={requestSending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={requestSending}
                >
                  {requestSending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThingDetailPage;
